import React, { useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageTitleContext';
import { generateSlug } from '../utils/slugUtils';
import { useGuestSaves, toggleGuestSave, removeGuestDraft } from '../hooks/useGuestSaves';

// Guest-visible "saved without an account" page (issue #13). Reads localStorage
// snapshots written by RestaurantDetail. Authenticated users are redirected to
// their profile, where the synced server-side saves live.
function SavedSection({ kind, title, emptyIcon, emptyText }) {
  const items = useGuestSaves(kind);

  if (items.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <div style={{ fontSize: '2rem' }}>{emptyIcon}</div>
        <p className="mb-0">{emptyText}</p>
      </div>
    );
  }

  return (
    <Row xs={1} sm={2} md={3} className="g-3">
      {items.map((item) => {
        // A saved item is a restaurant by default, or a dish (#102, target_type "food").
        const isFood = item.target_type === 'food';
        const to = isFood
          ? `/food/${item.id}/${generateSlug(item.name || '')}`
          : `/restaurant/${item.id}/${generateSlug(item.name)}`;
        return (
        <Col key={`${item.target_type || 'restaurant'}-${item.id}`}>
          <Card className="h-100 shadow-sm">
            {item.image && (
              <Link to={to}>
                <Card.Img
                  variant="top"
                  src={item.image}
                  alt={item.name}
                  style={{ height: '140px', objectFit: 'cover' }}
                />
              </Link>
            )}
            <Card.Body className="d-flex flex-column">
              <Card.Title className="h6 mb-1">
                <Link
                  to={to}
                  className="text-decoration-none"
                >
                  {isFood ? `🍽️ ${item.name}` : item.name}
                </Link>
              </Card.Title>
              {(item.city || item.state) && (
                <Card.Subtitle className="text-muted small mb-2">
                  {[item.city, item.state].filter(Boolean).join(', ')}
                </Card.Subtitle>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                className="mt-auto align-self-start"
                onClick={() => toggleGuestSave(kind, item)}
                title={`Remove from ${title.toLowerCase()}`}
              >
                Remove
              </Button>
            </Card.Body>
          </Card>
        </Col>
        );
      })}
    </Row>
  );
}

// Guest review/visit drafts (issue #13). These haven't been published anywhere —
// they post for real when the guest signs in. Shown so the guest can see and
// discard them locally.
function DraftSection({ kind, emptyIcon, emptyText }) {
  const items = useGuestSaves(kind); // 'reviews' | 'visits'

  if (items.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <div style={{ fontSize: '2rem' }}>{emptyIcon}</div>
        <p className="mb-0">{emptyText}</p>
      </div>
    );
  }

  return (
    <Row xs={1} sm={2} className="g-3">
      {items.map((draft) => {
        const target = draft.target || {};
        const link = target.restaurant_id
          ? `/restaurant/${target.restaurant_id}/${generateSlug(target.name || '')}`
          : target.food_id
            ? `/food/${target.food_id}/${generateSlug(target.name || '')}`
            : null;
        const body = draft.review_text || draft.notes;
        return (
          <Col key={draft.draft_id}>
            <Card className="h-100 shadow-sm">
              <Card.Body className="d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start">
                  <Card.Title className="h6 mb-1">
                    {link ? (
                      <Link to={link} className="text-decoration-none">{target.name || 'View'}</Link>
                    ) : (target.name || 'Draft')}
                  </Card.Title>
                  {draft.failed
                    ? <Badge bg="danger" pill>Couldn't post</Badge>
                    : <Badge bg="secondary" pill>Draft</Badge>}
                </div>
                {draft.failed && (
                  // Quarantined after the server turned it down (#227). Sign in
                  // to try again or discard it.
                  <div className="small text-danger mb-1">
                    It was turned down when we tried to post it
                    {draft.last_error?.detail ? `: ${draft.last_error.detail}` : '.'} Sign in to try again.
                  </div>
                )}
                {typeof draft.rating === 'number' && (
                  <div className="text-warning mb-1" aria-label={`${draft.rating} out of 5 stars`}>
                    {'★'.repeat(draft.rating)}{'☆'.repeat(5 - draft.rating)}
                  </div>
                )}
                {kind === 'visits' && draft.meal_type && (
                  <Card.Subtitle className="text-muted small mb-1 text-capitalize">{draft.meal_type}</Card.Subtitle>
                )}
                {draft.title && <div className="fw-semibold small">{draft.title}</div>}
                {body && <p className="small text-muted mb-2">{body}</p>}
                {kind === 'visits' && draft.photo_count > 0 && (
                  <div className="small text-muted mb-2">
                    📷 {draft.photo_count} photo{draft.photo_count === 1 ? '' : 's'} — upload{draft.photo_count === 1 ? 's' : ''} when you sign in
                  </div>
                )}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mt-auto align-self-start"
                  onClick={() => removeGuestDraft(kind, draft.draft_id)}
                >
                  Discard draft
                </Button>
              </Card.Body>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
}

function SavedPage() {
  const { isAuthenticated } = useAuth();
  const { setPageTitle } = usePageTitle();

  useEffect(() => {
    setPageTitle('Saved');
  }, [setPageTitle]);

  // Logged-in users keep their saves server-side, shown on their profile.
  if (isAuthenticated) {
    return <Navigate to="/profile" replace />;
  }

  return (
    <Container className="py-4">
      <h2 className="mb-3">Saved</h2>

      <Alert variant="warning" className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <span>
          These are saved on this device only — they can be lost if you clear your
          browser, and they won't sync to your phone or other browsers.
        </span>
        <Button as={Link} to="/login" variant="primary" size="sm">
          Create an account to keep them
        </Button>
      </Alert>

      <h4 className="mt-4 mb-3">
        Favorites <Badge bg="danger">❤️</Badge>
      </h4>
      <SavedSection
        kind="favorites"
        title="Favorites"
        emptyIcon="🤍"
        emptyText="No favorites yet — tap the heart on a restaurant or dish to save it here."
      />

      <h4 className="mt-5 mb-3">
        Wishlist <Badge bg="primary">📌</Badge>
      </h4>
      <SavedSection
        kind="wishlist"
        title="Wishlist"
        emptyIcon="📍"
        emptyText="Nothing on your wishlist yet — tap the pin on a restaurant or dish to add it."
      />

      <h4 className="mt-5 mb-3">
        Review drafts <Badge bg="warning" text="dark">⭐</Badge>
      </h4>
      <DraftSection
        kind="reviews"
        emptyIcon="📝"
        emptyText="No review drafts yet — write one on any restaurant or dish and it'll wait here until you sign in."
      />

      <h4 className="mt-5 mb-3">
        Visit drafts <Badge bg="info">🍽️</Badge>
      </h4>
      <DraftSection
        kind="visits"
        emptyIcon="📅"
        emptyText="No visit drafts yet — log a visit on a restaurant and it'll wait here until you sign in."
      />
    </Container>
  );
}

export default SavedPage;
